import { CompareSchedules } from "event-definitions"
import { ensureArray } from "lib/ensure-array"
import { attributeMap, onlyStandardProperties } from "routes/schedule/compare/register-task-comparitor"
import noop from "lib/noop"
import { SubsequentWrapper } from "routes/schedule/compare/subsequent-wrapper"
import { Bound } from "lib/@components/binding/Bound"
import { getComparisonNormalizeString } from "routes/schedule/compare/get-comparison-normalizer"

function wrap({ compare, to, target, comparedWith }) {
    return function mapIt(item) {
        return (
            <SubsequentWrapper
                compare={compare}
                content={item}
                to={to}
                target={target}
                comparedWith={comparedWith}
                children={item}
            />
        )
    }
}

export function registerComparitor(name, conflictRenderer, predicate = () => true) {
    CompareSchedules.handleOnce(({ differences, compare, to, target, comparedWith, ...params }) => {
        const normalizeString = getComparisonNormalizeString(compare, to, target)
        let v1 = Object.get(compare, name, true)
        let v2 = Object.get(to, name, true)
        if (typeof v1 === "string") {
            v1 = normalizeString(v1)
        }
        if (typeof v2 === "string") {
            v2 = normalizeString(v2)
        }
        const shouldRender =
            v1 !== undefined || v2 !== undefined
                ? onlyStandardProperties(v1) !== onlyStandardProperties(v2) && predicate(v1, v2)
                : true
        let result = null
        if (shouldRender) {
            const rendered = conflictRenderer({ ...params, compare, to, differences, target, comparedWith, v1, v2 })
            // A renderer returning null explicitly signals "no difference" (used by
            // optional-field comparitors such as the FIQ-1570 client fields); wrapping
            // null in a Bound would push an invisible entry that still counts towards
            // comparisons.length. undefined keeps the legacy behaviour (the guidance
            // comparitor returns undefined after pushing its own differences).
            if (rendered !== null) {
                result = (
                    <Bound to={to} compare={compare} target={target} comparedWith={comparedWith}>
                        {rendered}
                    </Bound>
                )
            }
        }
        if (result && (result.type || Array.isArray(result))) {
            const subTasks = ensureArray(result).map(wrap({ ...params, compare, to, target, comparedWith }))
            subTasks.forEach((task) =>
                attributeMap.get(task)
                    ? noop()
                    : attributeMap.set(task, {
                          ...params,
                          compare,
                          to,
                          name,
                          target,
                          comparedWith,
                      })
            )
            differences.push(...subTasks)
        }
    })
}
